import * as tus from "tus-js-client";

/**
 * TUS 슬라이드 업로드 공용 헬퍼.
 *
 * 이어올리기 정보(fingerprint → 업로드 URL)는 파일 이름·크기·수정시각·endpoint 로만 식별되고
 * 메타데이터(postId, postCaseId, slidename …)는 식별에 쓰이지 않는다.
 * 서버는 업로드 "생성" 시점의 메타데이터로 슬라이드를 등록하므로,
 * 다른 대상으로 올리는 업로드를 이어올리면 엉뚱한 게시물/Case 에 등록된다.
 */

// 중단 직후엔 서버가 직전 PATCH 를 아직 처리 중(423 Locked)일 수 있어 DELETE 를 몇 번 재시도한다
const TERMINATE_RETRY_DELAYS = [500, 1500, 3000];

const urlStorageOf = (upload: tus.Upload) =>
  upload.options.urlStorage ?? tus.defaultOptions.urlStorage;

const sameMetadata = (a: Record<string, string> = {}, b: Record<string, string> = {}) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).every((k) => (a[k] ?? "") === (b[k] ?? ""));
};

/** 브라우저에 저장된 이어올리기 정보 하나 삭제 (서버 쪽 데이터는 건드리지 않음) */
const forgetPreviousUpload = async (upload: tus.Upload, previous: any) => {
  if (!previous?.urlStorageKey) return;
  try {
    await urlStorageOf(upload).removeUpload(previous.urlStorageKey);
  } catch (e) {
    console.warn("tus url storage cleanup failed:", e);
  }
};

/**
 * 같은 대상(메타데이터가 완전히 같은) 업로드만 이어올린다.
 * 대상이 다른 이전 업로드는 이어올리기 정보만 지운다.
 * — 서버의 미완료 데이터는 다른 탭에서 진행 중일 수도 있어 삭제하지 않고, 서버 만료 정리(1일)에 맡긴다.
 */
export const resumeOnlySameTarget = async (upload: tus.Upload) => {
  const previous: any[] = await upload.findPreviousUploads();
  const expected = upload.options.metadata ?? {};
  const same = previous.find((p) => sameMetadata(p.metadata, expected));

  await Promise.all(previous.filter((p) => p !== same).map((p) => forgetPreviousUpload(upload, p)));
  if (same) upload.resumeFromPreviousUpload(same);
};

/**
 * 사용자가 중지한 업로드 정리.
 * 1) 이 파일의 이어올리기 정보 삭제
 * 2) 서버의 미완료 업로드 삭제 (tus termination, DELETE)
 *    — 실패해도 서버 TusCleanUpScheduler 가 만료 시간 뒤 정리한다.
 */
export const discardUpload = async (upload: tus.Upload) => {
  try {
    const previous = await upload.findPreviousUploads();
    await Promise.all(previous.map((p) => forgetPreviousUpload(upload, p)));
  } catch (e) {
    console.warn("tus url storage cleanup failed:", e);
  }

  if (upload.url) {
    try {
      await tus.Upload.terminate(upload.url, {
        ...upload.options,
        retryDelays: TERMINATE_RETRY_DELAYS,
        // createSlideUpload 의 재시도 판단·요청 훅은 업로드용이라 삭제 요청에는 쓰지 않는다
        // (중지 상태에선 재시도를 막으므로 그대로 두면 423 재시도가 동작하지 않는다)
        onShouldRetry: null,
        onBeforeRequest: undefined,
        onAfterResponse: undefined,
      });
    } catch (e) {
      console.warn("tus terminate failed (서버 만료 정리에 맡김):", e);
    }
  }
};

// ─────────────────────────────────────────────────────────────
// 재시도 + 멈춤 감지
// ─────────────────────────────────────────────────────────────

/** 일시적인 네트워크 오류·5xx 재시도 간격. 진행이 있으면 tus 가 시도 횟수를 다시 0 으로 돌린다 */
export const SLIDE_UPLOAD_RETRY_DELAYS = [1000, 5000, 15000, 30000, 60000];

/** 이 시간 동안 요청·응답·진행이 전혀 없으면 연결이 멈춘 것으로 보고 다시 연결한다 */
export const SLIDE_UPLOAD_STALL_TIMEOUT_MS = 2 * 60 * 1000;

const STALL_CHECK_INTERVAL_MS = 10 * 1000;

/** 진행 없이 연속으로 다시 연결한 횟수가 이를 넘으면 실패 처리 */
const MAX_STALL_RESTARTS = 5;

/** 멈춤 감지로 재연결을 반복해도 진행되지 않아 포기했을 때의 에러 */
export class UploadStalledError extends Error {
  constructor(fileName: string) {
    super(`'${fileName}' 업로드가 진행되지 않아 중단했습니다.`);
    this.name = "UploadStalledError";
  }
}

// 네트워크 오류(응답 없음), 409(offset 불일치), 423(직전 PATCH 처리 중), 5xx 만 재시도.
// 401/403/404 등 다시 보내도 결과가 같은 오류는 재시도하지 않는다
const isRetryableError = (err: tus.DetailedError) => {
  const status = err?.originalResponse?.getStatus?.() ?? 0;
  if (!status) return true;
  return status === 409 || status === 423 || status >= 500;
};

type UploadOptions = ConstructorParameters<typeof tus.Upload>[1];

interface SlideUploadControl {
  /** 사용자가 중지했는지 (useTusCancel.isCancelled). 중지되면 재시도·재연결하지 않는다 */
  isCancelled?: () => boolean;
}

/**
 * 재시도와 멈춤 감지가 붙은 슬라이드 업로드 생성.
 *
 * - 재시도: 일시적인 오류는 SLIDE_UPLOAD_RETRY_DELAYS 간격으로 서버가 받은 지점부터 다시 보낸다.
 * - 멈춤 감지: tus 요청에는 시간 제한이 없어 연결이 조용히 끊기면 오류 없이 영원히 기다린다.
 *   SLIDE_UPLOAD_STALL_TIMEOUT_MS 동안 아무 진행이 없으면 요청을 끊고 받은 지점부터 다시 연결한다.
 *
 * 파일 바이트를 모두 보낸 뒤에는 둘 다 하지 않는다.
 * 마지막 요청은 서버가 익명화·썸네일까지 처리하느라 오래 걸리는데, 이때 끊고 다시 보내면
 * 서버가 이미 정리한 업로드를 새로 올려 슬라이드가 중복 등록될 수 있다.
 * 진행 이벤트를 놓쳐 이를 판단하지 못해도, 한 업로드에서 생성(POST)은 한 번만 허용해 중복 등록을 막는다.
 */
export const createSlideUpload = (
  file: File,
  options: UploadOptions,
  control: SlideUploadControl = {},
): tus.Upload => {
  let allSent = false;
  let finished = false;
  let created = false;
  let recreationBlocked = false;
  let lastActivityAt = Date.now();
  let lastProgressBytes = -1;
  let stallRestarts = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  const stopWatchdog = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  const isCancelled = () => control.isCancelled?.() ?? false;

  const handleStall = async () => {
    stallRestarts++;
    if (stallRestarts > MAX_STALL_RESTARTS) {
      finished = true;
      stopWatchdog();
      await upload.abort(false).catch(() => undefined);
      options.onError?.(new UploadStalledError(file.name));
      return;
    }

    console.warn(`[tus] '${file.name}' ${SLIDE_UPLOAD_STALL_TIMEOUT_MS / 1000}초간 진행 없음 → 재연결 (${stallRestarts}/${MAX_STALL_RESTARTS})`);
    lastActivityAt = Date.now();
    // 서버의 미완료 업로드는 남겨두고(abort(false)) 받은 지점부터 이어서 보낸다
    await upload.abort(false).catch(() => undefined);
    if (finished || allSent || recreationBlocked || isCancelled()) return;
    upload.start();
  };

  const startWatchdog = () => {
    if (timer || finished || allSent) return;
    timer = setInterval(() => {
      if (finished || allSent || isCancelled()) {
        stopWatchdog();
        return;
      }
      if (Date.now() - lastActivityAt >= SLIDE_UPLOAD_STALL_TIMEOUT_MS) {
        void handleStall();
      }
    }, STALL_CHECK_INTERVAL_MS);
  };

  const upload = new tus.Upload(file, {
    retryDelays: SLIDE_UPLOAD_RETRY_DELAYS,
    ...options,
    onShouldRetry: (err) => !allSent && !recreationBlocked && !isCancelled() && isRetryableError(err),
    onBeforeRequest: async (req) => {
      lastActivityAt = Date.now();
      // 재시도·재연결 중 HEAD 가 404 면 tus 는 업로드를 새로 만들어 파일 전체를 다시 올린다.
      // 서버가 이미 처리를 끝내고 지운 업로드일 수 있어(슬라이드 중복 등록) 한 업로드에서 생성은 한 번만 허용한다
      if (req.getMethod() === "POST") {
        if (created) {
          recreationBlocked = true;
          throw new Error(`'${file.name}' 업로드를 새로 만들지 않고 중단합니다. (서버에서 이미 처리됐을 수 있음)`);
        }
        created = true;
      }
      startWatchdog();
      await options.onBeforeRequest?.(req);
    },
    onAfterResponse: async (req, res) => {
      lastActivityAt = Date.now();
      await options.onAfterResponse?.(req, res);
    },
    onProgress: (bytesSent, bytesTotal) => {
      lastActivityAt = Date.now();
      if (bytesSent > lastProgressBytes) {
        lastProgressBytes = bytesSent;
        stallRestarts = 0;
      }
      if (bytesSent === bytesTotal) {
        allSent = true;
        stopWatchdog();
      }
      options.onProgress?.(bytesSent, bytesTotal);
    },
    onSuccess: () => {
      finished = true;
      stopWatchdog();
      options.onSuccess?.();
    },
    onError: (err) => {
      if (finished) return;
      finished = true;
      stopWatchdog();
      options.onError?.(err);
    },
  });

  return upload;
};
